import type { CityConfig } from "@/pages/city-template/types";

export const surpriseConfig: CityConfig = {
  city: "Surprise",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "surprise",
  route: "/cities/surprise",
  title: "Emergency & Expedited Passport Services Surprise AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Surprise, AZ — as fast as 24 hours. No Surprise passport agency. Fast Passport Center couriers to the Western Passport Center in Tucson, about 130 miles southeast. A+ BBB rated. Serving Northwest Valley families, Sun City and Sun City West active adults, spring training visitors, and Ottawa University students.",
  h1: "Emergency & Expedited Passport Services in Surprise, AZ",
  heroSubtitle: "Surprise is one of the fastest-growing cities in the Northwest Valley, about 130 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're a Northwest Valley family planning an international vacation, a Sun City West retiree heading on a European river cruise, a spring training baseball fan extending the season with international travel, or an Ottawa University student studying abroad.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Surprise%20Arizona%20Northwest%20Valley%20suburban%20landscape%20with%20modern%20homes%20and%20desert%20landscaping%20under%20bright%20blue%20sky%2C%20planned%20community%20with%20palm%20trees%20and%20mountain%20views%20in%20the%20distance%2C%20clean%20wide%20streets%20with%20southwestern%20architecture%2C%20warm%20golden%20afternoon%20light%2C%20professional%20landscape%20photography%2C%20no%20people%20visible&width=1800&height=700&seq=su-hero-001&orientation=landscape",
  heroImageAlt: "Surprise Arizona Northwest Valley suburban landscape with desert mountain backdrop",
  geoPosition: "33.6292;-112.3679",
  geoPlacename: "Surprise, Arizona",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 130 miles from Surprise — about 2 hours via I-10",
  },
  nearbyCitiesZips: [
    { name: "Surprise", zips: "85374, 85378, 85379, 85387, 85388" },
    { name: "Sun City", zips: "85351, 85373" },
    { name: "Sun City West", zips: "85375" },
    { name: "El Mirage", zips: "85335" },
    { name: "Peoria", zips: "85345, 85382, 85383" },
    { name: "Waddell", zips: "85355" },
    { name: "Youngtown", zips: "85363" },
    { name: "Litchfield Park", zips: "85340" },
    { name: "Buckeye", zips: "85326, 85396" },
    { name: "Goodyear", zips: "85338, 85395" },
    { name: "Glendale", zips: "85301, 85302, 85308" },
    { name: "Wittmann", zips: "85361" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 130 miles from Surprise" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Northwest Valley Hub", desc: "Serving Surprise, Sun City, Sun City West, El Mirage, and all Northwest Valley communities" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Surprise case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Peoria AZ", route: "/cities/peoria-az" },
    { name: "Expedited passport in Phoenix AZ", route: "/cities/phoenix" },
    { name: "Urgent passport in Tucson AZ", route: "/cities/tucson" },
    { name: "Arizona passport hub", route: "/states/arizona" },
    { name: "Fast passport in Glendale AZ", route: "/cities/glendale-az" },
    { name: "Emergency passport in Scottsdale AZ", route: "/cities/scottsdale" },
    { name: "Expedited passport in Goodyear AZ", route: "/cities/goodyear" },
  ],
  fpcOfficeAddress: "4742 N 24th St, Phoenix, AZ 85016",
  fpcOfficeNote: "Fast Passport Center serves Surprise remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson. Surprise is about 130 miles from Tucson via I-10 — roughly 4.5 hours round trip. While closer than many Arizona communities, that is still a significant drive that cuts into a workday. Our courier service eliminates this entirely so you can focus on planning your trip.",
  fpcOfficeDistance: "Serving all of the Northwest Valley and Sun Cities region",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Surprise, Sun City, Sun City West, El Mirage, and the Northwest Valley. From young families taking kids on their first international trips to active adult retirees embarking on world cruises, we help Northwest Valley travelers get passports fast.",
  nearbyCitiesIntro: "We serve Surprise and nearby communities including Sun City, Sun City West, El Mirage, Youngtown, Waddell, and the entire Northwest Valley — from the White Tank Mountains to Loop 303.",
  howItWorksIntro: "Most Surprise travelers complete everything online and ship documents overnight. At about 130 miles from Tucson along I-10, Fast Passport Center's courier service provides faster turnaround than standard USPS processing for Northwest Valley residents — essential for fast-growing communities where international travel is increasing year over year.",
  mistakesIntro: "Surprise travelers, including active adult retirees and Northwest Valley families, face unique passport challenges specific to Arizona's retirement communities and rapidly growing suburbs. Here are the most common mistakes we see.",
  testimonialsIntro: "Surprise and Northwest Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Surprise? Here are the most common ones we hear from Northwest Valley residents.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No 4.5-hour round-trip drive to Tucson",
        "Ideal for Sun City West retirees on world cruises",
        "Perfect for Northwest Valley families on vacation",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "130 miles to Tucson (2-hour drive each way)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Surprise is 130 miles from Tucson. Even a 4.5-hour round trip is unnecessary — our couriers deliver to the Western Passport Center for you.",
      bullets: ["Northwest Valley coverage", "24–48 hour turnaround available", "Skip the half-day drive to Tucson"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Surprise adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Surprise and Northwest Valley"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Surprise and the Northwest Valley.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Surprise, Sun City West, and Northwest Valley residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in the Northwest Valley.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Surprise and Northwest Valley residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Surprise AZ", description: "Urgent passport processing for Surprise, AZ travelers via Western Passport Center courier — about 130 miles from Surprise." },
    { name: "Passport Renewal Surprise AZ", description: "Fast passport renewal for Surprise and Maricopa County, Arizona residents." },
    { name: "New Passport Surprise AZ", description: "First-time passport application assistance for Surprise and Northwest Valley residents." },
    { name: "Lost Passport Surprise AZ", description: "Passport replacement for lost or stolen passports for Surprise and Northwest Valley residents." },
    { name: "Child Passport Surprise AZ", description: "Minor passport services for Surprise and Northwest Valley families." },
    { name: "Name Change Passport Surprise AZ", description: "Passport name change service for Surprise, AZ residents." },
  ],
  testimonials: [
    {
      name: "The Hendersons",
      location: "Sun City West, AZ",
      rating: 5,
      text: "Retired couple who booked a 21-day European river cruise from Amsterdam to Budapest. Realized 3 weeks before departure that my passport was expiring in 4 months — and most European countries require 6 months validity. Fast Passport Center got me a renewal in 5 business days. The cruise was amazing. Could not believe how fast they worked.",
      service: "Expedited Renewal",
    },
    {
      name: "Jennifer K.",
      location: "Surprise, AZ",
      rating: 5,
      text: "Ottawa University student accepted into a study abroad program in Barcelona. Got my acceptance 3 weeks before the program start. I'd never had a passport. Fast Passport Center walked me through everything — DS-11, photos, where to go for my acceptance clerk visit. Passport in 6 business days. I'm typing this from Barcelona!",
      service: "New Passport",
    },
    {
      name: "The Martinez Family",
      location: "Surprise, AZ",
      rating: 5,
      text: "Family of four — two adults, two kids under 10. First international trip to Costa Rica over spring break. Fast Passport Center processed all four passports in 7 business days. The child passport guidance was essential — we had no idea both parents needed to be there in person. They saved us from a rookie mistake that would have delayed everything.",
      service: "Family Passport Package",
    },
    {
      name: "Bill R.",
      location: "Sun City, AZ",
      rating: 5,
      text: "Won a contest at my retirement community — an all-expenses-paid trip to Australia for two! Realized my passport had been expired for 3 years and the trip was in 3 weeks. Fast Passport Center got me a new passport in 4 business days. My wife and I had the trip of a lifetime. Their service literally saved the day.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "su-1",
      name: "Surprise City Clerk's Office",
      type: "Municipal Passport Acceptance Facility",
      address: "16000 N Civic Center Plaza",
      city: "Surprise",
      state: "AZ",
      zip: "85374",
      phone: "(623) 222-1200",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM (passport services by appointment)",
      distance: "Surprise",
      website: "https://www.surpriseaz.gov/211/Passport-Services",
      websiteLabel: "Surprise Passport Services",
      notes: "Primary municipal passport acceptance for Surprise residents. Conveniently located at the Civic Center. Appointment required — book online.",
      icon: "ri-building-line",
    },
    {
      id: "su-2",
      name: "Surprise Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "14955 W Bell Rd",
      city: "Surprise",
      state: "AZ",
      zip: "85374",
      phone: "(623) 546-3582",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Surprise",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient USPS acceptance facility on Bell Road in central Surprise. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "su-3",
      name: "Sun City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10001 W Bell Rd",
      city: "Sun City",
      state: "AZ",
      zip: "85351",
      phone: "(623) 933-3643",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles east in Sun City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Sun City and eastern Surprise residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "su-4",
      name: "Sun City West Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "19005 N RH Johnson Blvd",
      city: "Sun City West",
      state: "AZ",
      zip: "85375",
      phone: "(623) 584-0973",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles north in Sun City West",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Sun City West and northern Surprise residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "su-5",
      name: "El Mirage Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "12401 W Thunderbird Rd",
      city: "El Mirage",
      state: "AZ",
      zip: "85335",
      phone: "(623) 875-1063",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles east in El Mirage",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for El Mirage, Youngtown, and eastern Surprise residents.",
      icon: "ri-mail-line",
    },
    {
      id: "su-6",
      name: "Peoria City Clerk's Office",
      type: "Municipal Passport Acceptance Facility",
      address: "8401 W Monroe St",
      city: "Peoria",
      state: "AZ",
      zip: "85345",
      phone: "(623) 773-7340",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM (passport services by appointment)",
      distance: "10 miles southeast in Peoria",
      website: "https://www.peoriaaz.gov/government/departments/city-clerk/passport-services",
      websiteLabel: "Peoria Passport Services",
      notes: "Well-established municipal passport acceptance serving the greater Northwest Valley. Appointment required.",
      icon: "ri-building-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "130 miles — 2-hr drive each way, need appointment", fpc: "We drive for you — no 4.5-hour round trip needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-passport-line",
      title: "The 6-Month Validity Rule — Retiree Trap",
      description: "Many Sun City and Sun City West active adults book international cruises and tours without checking their passport expiration date. Many countries require at least 6 months of passport validity beyond your planned return date. An expiring-soon passport can derail an entire retirement dream trip. Check your passport now — not 2 weeks before you leave.",
    },
    {
      icon: "ri-calendar-line",
      title: "Spring Training Season Delays",
      description: "Surprise is a major spring training destination hosting the Kansas City Royals and Texas Rangers. From February through March, acceptance facility appointments in the Northwest Valley can fill up 2–3 weeks in advance due to increased demand. If you need a first-time passport during spring training season, book your acceptance facility visit well in advance.",
    },
    {
      icon: "ri-file-list-line",
      title: "Renewal Form Confusion — DS-82 vs. DS-11",
      description: "Many Surprise residents are unsure whether they can renew by mail (DS-82) or must apply in person (DS-11). You can use DS-82 by mail only if: your most recent passport was issued within the last 15 years, you were 16 or older when it was issued, it is undamaged, and you have the same name (or can document a name change). Otherwise, you need DS-11 and an in-person acceptance facility visit.",
    },
    {
      icon: "ri-plane-line",
      title: "Assuming Domestic REAL ID Works for International Travel",
      description: "Surprise residents who have updated to an Arizona REAL ID may mistakenly think it can substitute for a passport on international trips. REAL ID is for domestic air travel only. For any international travel — including Canada and Mexico — you need a valid passport book. REAL ID does not work at international borders.",
    },
    {
      icon: "ri-parent-line",
      title: "Youth Sports and School Group Travel — Late Applications",
      description: "Surprise families with kids in travel sports leagues and school international programs often apply for children's passports too late. Minor passport applications require both parents to appear in person. With Surprise being one of Arizona's fastest-growing family communities, acceptance facility appointments book up quickly during school breaks.",
    },
    {
      icon: "ri-image-line",
      title: "Photo Quality — Big Box vs. Professional",
      description: "While big-box retailers in Surprise offer passport photo services, quality varies. Passport photos must be exactly 2x2 inches, white background, no shadows, neutral expression, and no glasses (as of 2016). Acceptable photos look deceptively simple — small imperfections cause rejection. Verify your photos at the time they are taken.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Surprise, Sun City, or the West Valley." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — 130 miles southeast via I-10." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Surprise or Northwest Valley address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Surprise from a passport agency?",
      a: "Surprise is approximately 130 miles from the Western Passport Center in Tucson — about a 2-hour drive each way via I-10. That is a 4.5-hour round trip. While closer than many Arizona communities, it still requires taking time off work or missing personal commitments. Fast Passport Center's courier service handles the Tucson trip for you.",
    },
    {
      q: "Does Surprise have its own passport acceptance facility?",
      a: "Yes. The Surprise City Clerk's Office at the Civic Center (16000 N Civic Center Plaza) is a municipal passport acceptance facility. The Surprise Post Office at 14955 W Bell Road also offers USPS acceptance services. Both require appointments and operate during regular business hours on weekdays. The City Clerk's Office allows online appointment booking — the most convenient option in the Northwest Valley.",
    },
    {
      q: "How fast can I get an emergency passport in Surprise?",
      a: "Emergency processing is typically 24–48 hours for Surprise residents. Our couriers deliver directly to the Western Passport Center in Tucson — 130 miles away. This saves you a 4.5-hour round-trip drive. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "I'm a Sun City West retiree — my cruise departs in 3 weeks and my passport expires in 5 months. Can you help?",
      a: "Yes — this is one of the most common scenarios we handle for Sun City and Sun City West residents. Many countries require 6 months of passport validity beyond your return date. Our expedited renewal service can get you a new passport in 2–9 business days, well before your cruise departure. Do not wait — start the process immediately.",
    },
    {
      q: "Do I need a passport if I'm only going to Mexico or Canada?",
      a: "Yes. All international air travel requires a valid passport book, including flights to Mexico and Canada. Even for land border crossings, a passport card or enhanced driver's license is required (though a passport book is recommended). REAL ID does not work at international borders.",
    },
    {
      q: "Can I renew my passport by mail in Surprise?",
      a: "Adult renewals (16 or older, passport issued within 15 years, same name, undamaged passport) can be done by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — much faster than the 8–11 week standard USPS timeline. If you do not qualify for DS-82 renewal, you must apply in person using DS-11 at an acceptance facility.",
    },
    {
      q: "Where can I get passport photos in Surprise?",
      a: "Passport photo options in Surprise include Walgreens at 13731 W Bell Road, CVS Pharmacy at 15201 W Bell Road, and the UPS Store at 17071 W Bell Road. The Surprise City Clerk's Office also offers passport photo services during acceptance facility appointments. Always verify photos meet exact specifications before submitting.",
    },
    {
      q: "Do you serve Sun City West and El Mirage residents too?",
      a: "Yes. We serve all Northwest Valley communities including Sun City, Sun City West, El Mirage, Youngtown, Waddell, and the entire Surprise area. Our remote courier service works the same for all Northwest Valley residents — complete your application online and we handle the rest.",
    },
    {
      q: "What if I'm a spring training visitor who needs passport services while in Surprise?",
      a: "If you are in Surprise temporarily for spring training or seasonal visits and need passport services, you can still use Fast Passport Center. You will need to have your documents available and be able to receive your new passport at your Surprise or Arizona address. Processing takes 2–9 business days for expedited service.",
    },
    {
      q: "My child plays travel baseball and we have a tournament in the Dominican Republic — how fast can you get a child passport?",
      a: "Child passports require an in-person acceptance facility visit with both parents present. Once the acceptance facility appointment is complete, Fast Passport Center can process your child's passport in 2–9 business days via our expedited courier service. Start the process as soon as you know the tournament dates — acceptance facility appointments in Surprise fill quickly during school break periods.",
    },
    {
      q: "I'm an Ottawa University student accepted into study abroad — can you get me a passport fast?",
      a: "Absolutely. Ottawa University study abroad students are some of our most frequent Surprise clients. First-time passport applicants must complete an acceptance facility visit (we recommend the Surprise City Clerk's Office for their online booking system), then Fast Passport Center handles the fast turnaround. Processing in 2–9 business days is typical for expedited service.",
    },
    {
      q: "How does I-10 traffic affect getting to the Tucson passport agency?",
      a: "The 130-mile drive from Surprise to Tucson via I-10 can take 2 hours on a good day — but Phoenix metro traffic through the Broadway Curve and Casa Grande corridor frequently adds 30–60 minutes each way. An appointment at the Tucson agency requires an entire day commitment. Fast Passport Center's courier service eliminates this uncertainty entirely.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Surprise (Bell Rd)", address: "13731 W Bell Rd, Surprise, AZ 85374", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Surprise", address: "15201 W Bell Rd, Surprise, AZ 85374", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Surprise", address: "17071 W Bell Rd, Ste 100, Surprise, AZ 85374", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
    { name: "Walgreens — Surprise (Greenway Rd)", address: "15505 N Greenway Hayden Loop, Surprise, AZ 85374", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Sun City", address: "10050 W Bell Rd, Sun City, AZ 85351", hours: "Mon–Sun: 8AM–9PM" },
    { name: "FedEx Office — Peoria", address: "7757 W Bell Rd, Peoria, AZ 85382", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Surprise", address: "13009 W Bell Rd, Surprise, AZ 85378", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Surprise, Sun City West, and Northwest Valley residents. Conveniently located on Bell Road.", featured: true },
    { name: "AZ MVD — Glendale (NW)", address: "16380 N 59th Ave, Glendale, AZ 85306", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves northern Glendale, Peoria, and eastern Surprise residents. About 15 miles east.", featured: false },
    { name: "AZ MVD — Goodyear", address: "170 N Litchfield Rd, Goodyear, AZ 85338", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Goodyear, Litchfield Park, and southern Surprise residents. About 20 miles south.", featured: false },
  ],
  airports: [
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "35 miles southeast of Surprise",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 40–50 minutes via Loop 101 and I-10. Primary airport for Northwest Valley residents traveling internationally.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: true,
    },
    {
      code: "AZA",
      name: "Phoenix-Mesa Gateway Airport",
      distance: "55 miles southeast of Surprise",
      address: "6033 S Sossaman Rd, Mesa, AZ 85212",
      note: "Secondary Phoenix metro airport with Allegiant and other low-cost carrier domestic service. Limited international routes. About 1 hour via Loop 303 and US-60.",
      flightUrl: "https://www.gatewayairport.com/",
      flightLabel: "AZA Airport",
      featured: false,
    },
    {
      code: "TUS",
      name: "Tucson International Airport",
      distance: "130 miles southeast of Surprise",
      address: "7250 S Tucson Blvd, Tucson, AZ 85756",
      note: "Southern Arizona international airport with domestic and limited international service. About 2 hours via I-10.",
      flightUrl: "https://www.flytucson.com/",
      flightLabel: "TUS Airport",
      featured: false,
    },
  ],
};